import { useCatalogStore } from "application/contexts/catalog/CatalogStore";
import { useIdle } from "application/contexts/idle/IdleContext";
import { useScreenStore } from "application/contexts/screen/ScreenStore";
import { UseBarCode } from "areas/barCode/UseBarCode";
import { Product } from "domains/catalog/aggregates/Product";
import { SeccionedProduct } from "domains/catalog/aggregates/SeccionedProduct";
import { ProdutoTipo } from "Infrastructure/interfaces/PedidoMeep";
import { ICartContext } from "modules/cart/domain/interfaces/IUseCaseCart";
import { useCartStore } from "modules/cart/infra/store/CartStore";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";

export interface CategoryScrollRef {
  categoryScroll: (index: number) => void;
}

export const useCatalogHook = () => {
  const [setConfigCart, setSuggestionProducts] = useCartStore(
    (state: ICartContext) => [state.setConfigCart, state.setSuggestionProducts]
  );

  const { storeId } = useParams<{ storeId: string }>();
  const { replace } = useHistory();
  const {
    catalog,
    modalValues,
    openProductDetail,
    closeProductDetail,
    suggestionProductIds,
  } = useCatalogStore();
  const { handleActiveIdleTimer } = useIdle();
  const { orientation } = useScreenStore();

  const { focus, refDiv } = UseBarCode();

  const [categoryPosition, setCategoryPosition] = useState(0);
  const [animate, setAnimate] = useState(false);
  const [hideCategory, setHideCategory] = useState(false);
  const [weighingSelectProducts, setWeighingSelectProducts] = useState<Product[]>([]);

  const productListRef = useRef<CategoryScrollRef>(null);

  const store = useMemo(
    () => catalog?.stores[parseInt(storeId)],
    [catalog, storeId]
  );

  const suggestionProductIdSet = useMemo(() => {
    return new Set(suggestionProductIds);
  }, [suggestionProductIds]);

  const closeCategory = useCallback(() => {
    setHideCategory(true);
  }, []);

  const openCategory = useCallback(() => {
    setHideCategory(false);
  }, []);

  const handleCategoryClick = useCallback((index: number) => {
    productListRef.current?.categoryScroll(index);
  }, []);

  const handleChangeCategory = useCallback((position: number) => {
    setCategoryPosition(position);
  }, []);

  useEffect(() => {
    if (store) {
      const sugProducts: Product[] = store.seccionedProduct
        .flatMap((it: SeccionedProduct) => it.products)
        .filter((p: Product) => suggestionProductIdSet.has(p.id));
      setSuggestionProducts(sugProducts);

      const allProducts: Product[] = store.seccionedProduct.flatMap(
        (it: SeccionedProduct) => it.products
      );

      if (allProducts.length === 1 && allProducts[0].type === ProdutoTipo.Kilo) {
        setHideCategory(true);
        openProductDetail(allProducts[0].id);
      }
    }
  }, [openProductDetail, setSuggestionProducts, store, suggestionProductIdSet]);

  // Timestamp do último fechamento do modal — evita reabrir enquanto balança ainda lê peso saindo
  const lastModalCloseRef = useRef<number>(0);
  const modalWasOpenRef = useRef<boolean>(false);
  const REOPEN_COOLDOWN_MS = 1500;

  // Marca timestamp apenas quando o modal fecha (não no mount inicial)
  useEffect(() => {
    const isOpen = modalValues.open || !!modalValues.openWeighing;
    if (isOpen) {
      modalWasOpenRef.current = true;
    } else if (modalWasOpenRef.current) {
      lastModalCloseRef.current = Date.now();
    }
  }, [modalValues.open, modalValues.openWeighing]);

  // Solicita ao RN iniciar a balança em background assim que o catálogo está montado
  useEffect(() => {
    if (!("RNBridge" in window)) return;
    window.RNBridge.callNative("startBackgroundScale").catch(() => {});
  }, []);

  // Auto-open: abre modal do produto kilo automaticamente quando balança detecta peso
  useEffect(() => {
    if (!("RNBridge" in window)) return;

    const unsubscribe = window.RNBridge.on("onWeightDetected", () => {
      // Não abre se já tem modal de produto ou seleção aberto
      if (modalValues.open || modalValues.openWeighing || weighingSelectProducts.length > 0) return;

      // Cooldown após fechar — evita reabrir enquanto balança ainda lê o peso saindo
      if (Date.now() - lastModalCloseRef.current < REOPEN_COOLDOWN_MS) return;

      const kiloProducts = store?.seccionedProduct
        .flatMap((sp: SeccionedProduct) => sp.products)
        .filter((p: Product) => p.type === ProdutoTipo.Kilo) ?? [];

      if (kiloProducts.length === 0) return;

      if (kiloProducts.length === 1) {
        // 1 produto: abre direto
        openProductDetail(kiloProducts[0].id);
      } else {
        // N produtos: mostra seletor
        setWeighingSelectProducts(kiloProducts);
      }
    });

    return () => unsubscribe();
  }, [store, modalValues, openProductDetail, weighingSelectProducts]);

  useEffect(() => {
    if (modalValues.open || modalValues?.openWeighing) {
      setTimeout(() => setAnimate(true), 50);
    } else {
      setAnimate(false);
    }
  }, [modalValues, modalValues.open]);

  useEffect(() => {
    handleActiveIdleTimer();
  }, [handleActiveIdleTimer]);

  useEffect(() => {
    setConfigCart({
      fullscreen: false,
      disableCloseButton: false,
      openOnCreate: false,
    });
  }, [setConfigCart]);

  const closeProductDetailModal = useCallback(() => {
    setAnimate(false);
    setTimeout(() => {
      closeProductDetail();
    }, 300);
  }, [closeProductDetail]);

  const onWeighingProductSelect = useCallback((product: Product) => {
    setWeighingSelectProducts([]);
    openProductDetail(product.id);
  }, [openProductDetail]);

  const onWeighingSelectClose = useCallback(() => {
    setWeighingSelectProducts([]);
    lastModalCloseRef.current = Date.now();
  }, []);

  const filterSeccionedProducts = useCallback(
    (seccionedProducts?: SeccionedProduct[]) => {
      if (!seccionedProducts) {
        return [];
      }
      let weighingProducts: SeccionedProduct = {
        id: "__weighing__",
        imageUrl: "",
        products: [],
        title: "Balança",
        categoryIcon: "weight",
      };
      let featuredList: SeccionedProduct = {
        id: "__featured__",
        imageUrl: "",
        products: [],
        title: "Destaques",
        categoryIcon: "featured",
      };
      const filteredSeccionedProducts = seccionedProducts
        .map((seccionedProduct) => ({
          ...seccionedProduct,
          products: seccionedProduct.products.filter((product) => {
            if (product.isFeatured) {
              featuredList.products.push(product);
            }
            if (product.type === ProdutoTipo.Kilo) {
              weighingProducts.products.push(product);
            }
            return (
              product.hideCatalog !== true && product.type !== ProdutoTipo.Kilo
            );
          }),
        }))
        .filter((seccionedProduct) => seccionedProduct.products.length > 0);

      if (
        featuredList.products.length > 0 ||
        weighingProducts.products.length > 0
      ) {
        if (
          featuredList.products.length > 0 &&
          weighingProducts.products.length > 0
        ) {
          return [weighingProducts, featuredList, ...filteredSeccionedProducts];
        } else if (featuredList.products.length > 0) {
          return [featuredList, ...filteredSeccionedProducts];
        } else {
          return [weighingProducts, ...filteredSeccionedProducts];
        }
      } else {
        return filteredSeccionedProducts;
      }
    },
    []
  );

  const seccionedProductSource = store?.seccionedProduct;
  const seccionedProductsFiltered = useMemo(() => {
    return filterSeccionedProducts(seccionedProductSource);
  }, [filterSeccionedProducts, seccionedProductSource]);

  return {
    filterSeccionedProducts,
    seccionedProductsFiltered,
    productListRef,
    categoryPosition,
    setCategoryPosition,
    handleCategoryClick,
    handleChangeCategory,
    modalValues,
    openProductDetail,
    closeProductDetailModal,
    store,
    replace,
    animate,
    hideCategory,
    closeCategory,
    openCategory,
    orientation,
    focus,
    refDiv,
    weighingSelectProducts,
    onWeighingProductSelect,
    onWeighingSelectClose,
  };
};
